import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Briefcase,
  Building2,
  FileText,
  Landmark,
  Loader2,
  TrendingUp,
} from 'lucide-react';
import { apiClient } from '../../lib/api';
import { CompaniesTab } from './case/CompaniesTab';
import { FinancialsTab } from './case/FinancialsTab';
import { CreditPortfolioTab } from './case/CreditPortfolioTab';
import { AnalysisTab } from './case/AnalysisTab';
import { MemoTab } from './case/MemoTab';
import type { Case } from './case/types';

type TabKey = 'companies' | 'financials' | 'portfolio' | 'analysis' | 'memo';

const TABS: {
  key: TabKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { key: 'companies', label: 'חברות', icon: Building2 },
  { key: 'financials', label: 'דוחות כספיים', icon: TrendingUp },
  { key: 'portfolio', label: 'תיק אשראי', icon: Landmark },
  { key: 'analysis', label: 'ניתוח', icon: Briefcase },
  { key: 'memo', label: 'תיק פנימי', icon: FileText },
];

export function CaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const [tab, setTab] = useState<TabKey>('companies');

  const caseQuery = useQuery<Case>({
    queryKey: ['case', caseId],
    queryFn: () => apiClient.get<Case>(`/cases/${caseId}`),
    enabled: !!caseId,
  });

  if (!caseId) {
    return <div className="text-sm text-slate-600">חסר מזהה קייס.</div>;
  }

  return (
    <div>
      <Link
        to="/business"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-business-primary mb-2"
      >
        <ArrowRight className="w-4 h-4" />
        חזרה ללוח הבקרה
      </Link>

      <CaseHeader caseQuery={caseQuery} />

      <div className="border-b border-slate-200 mb-6 -mx-2">
        <nav className="flex gap-1 px-2 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                tab === key
                  ? 'border-business-primary text-business-primary'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {tab === 'companies' && <CompaniesTab caseId={caseId} />}
        {tab === 'financials' && <FinancialsTab caseId={caseId} />}
        {tab === 'portfolio' && <CreditPortfolioTab caseId={caseId} />}
        {tab === 'analysis' && <AnalysisTab caseId={caseId} />}
        {tab === 'memo' && <MemoTab caseId={caseId} />}
      </div>
    </div>
  );
}

function CaseHeader({
  caseQuery,
}: {
  caseQuery: ReturnType<typeof useQuery<Case>>;
}) {
  if (caseQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 mb-6 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        טוען קייס…
      </div>
    );
  }

  if (caseQuery.isError) {
    return (
      <div className="card mb-6 text-sm text-red-700 bg-red-50 border-red-200">
        שגיאה בטעינת הקייס: {(caseQuery.error as Error).message}
      </div>
    );
  }

  const c = caseQuery.data;
  if (!c) return null;

  return (
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-navy mb-1">{c.title}</h1>
      <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
        <span>
          סוג: {c.workflow_type === 'group_review' ? 'ניתוח קבוצה' : 'ניתוח עסקה'}
        </span>
        <span>סטטוס: {c.status}</span>
        {c.reference_number && <span>סימוכין: {c.reference_number}</span>}
        {c.base_year && <span>שנת בסיס: {c.base_year}</span>}
      </div>
    </div>
  );
}
